import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon } from 'lucide-react';
import type { OpdrachtgeverRij } from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Item, ItemActions, ItemContent, ItemGroup, ItemTitle } from '@/components/ui/item';
import { PersonenStapel } from '@/components/PersonenStapel';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '../../lib/apiClient';
import { useMaakOpdracht, usePersonen } from './personen.api';

/**
 * Waar de opdrachten worden ingericht.
 *
 * Een opdracht bijzetten is de handeling die hier het vaakst gebeurt, en die zit
 * daarom bij de opdrachtgever zelf: een plusje in de kop, een veld eronder,
 * Enter en klaar. Er stond één formulier bovenaan met een keuzelijst voor de
 * opdrachtgever, en dan kies je elke keer opnieuw waar het ook alweer bij hoorde.
 *
 * Een nieuwe opdrachtgever aanmaken gebeurt zelden en staat daarom onderaan.
 *
 * Verwijderen staat op de pagina van de opdracht zelf. Hier stond een prullenbak
 * per regel, maar dat is een zware knop naast een naam waar je meestal alleen op
 * klikt om te kijken wie erop zit.
 */
export function OpdrachtenTab() {
  const { data } = usePersonen();
  const [fout, setFout] = useState('');
  const [toevoegenBij, setToevoegenBij] = useState<string | null>(null);

  const opdrachtgevers = data?.opdrachtgevers ?? [];

  // Wie op welke opdracht zit, uit de personenlijst die er toch al is. Alleen
  // wie meedoet: het archief hoort niet mee te tellen bij een lopende opdracht.
  const opOpdracht = useMemo(() => {
    const uit = new Map<string, Array<{ id: string; naam: string }>>();
    for (const p of data?.personen ?? []) {
      if (!p.actief || p.gearchiveerdOp) continue;
      for (const o of p.opdrachten) {
        const lijst = uit.get(o.id) ?? [];
        lijst.push({ id: p.id, naam: p.naam });
        uit.set(o.id, lijst);
      }
    }
    return uit;
  }, [data?.personen]);

  return (
    <div className="flex flex-col gap-4">
      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}

      {opdrachtgevers.length === 0 && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nog geen opdrachten.</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}

      {opdrachtgevers.map((gever) => (
        <Card key={gever.id}>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle>{gever.naam}</CardTitle>
            <Button
              aria-label={`Opdracht toevoegen bij ${gever.naam}`}
              onClick={() => setToevoegenBij(toevoegenBij === gever.id ? null : gever.id)}
              size="sm"
              variant="ghost"
            >
              <PlusIcon data-icon="inline-start" />
              Opdracht
            </Button>
          </CardHeader>
          <CardContent>
            <ItemGroup>
              {gever.opdrachten.map((opdracht) => (
                // `min-h`: een regel met avatars is anders hoger dan een regel
                // zonder, en dan staat de lijst scheef.
                <Item asChild className="min-h-14" key={opdracht.id} variant="muted">
                  <Link to={`/opdrachten/${opdracht.id}`}>
                    <ItemContent className="min-w-0">
                      <ItemTitle>{opdracht.naam}</ItemTitle>
                    </ItemContent>
                    <ItemActions>
                      <PersonenStapel
                        klikbaar={false}
                        personen={opOpdracht.get(opdracht.id) ?? []}
                      />
                    </ItemActions>
                  </Link>
                </Item>
              ))}

              {toevoegenBij === gever.id && (
                <NieuweOpdracht
                  gever={gever}
                  onFout={setFout}
                  onKlaar={() => setToevoegenBij(null)}
                />
              )}
            </ItemGroup>
          </CardContent>
        </Card>
      ))}

      <NieuweOpdrachtgever onFout={setFout} />
    </div>
  );
}

/** De regel die onder de lijst verschijnt zodra je op het plusje drukt. */
function NieuweOpdracht({
  gever,
  onKlaar,
  onFout,
}: {
  gever: OpdrachtgeverRij;
  onKlaar: () => void;
  onFout: (melding: string) => void;
}) {
  const maak = useMaakOpdracht();
  const [naam, setNaam] = useState('');

  async function opslaan() {
    if (!naam.trim()) return;
    onFout('');
    try {
      await maak.mutateAsync({ opdrachtgever: gever.naam, opdracht: naam });
      // Leeg en open laten staan: wie er één bijzet, zet er meestal meer bij.
      setNaam('');
    } catch (err) {
      onFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <Item>
      <ItemContent>
        <Input
          aria-label={`Naam van de nieuwe opdracht bij ${gever.naam}`}
          autoComplete="off"
          autoFocus
          onChange={(e) => setNaam(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void opslaan();
            }
            if (e.key === 'Escape') onKlaar();
          }}
          placeholder="Naam van de opdracht"
          value={naam}
        />
      </ItemContent>
      <ItemActions>
        <Button disabled={!naam.trim() || maak.isPending} onClick={() => void opslaan()} size="sm">
          {maak.isPending && <Spinner data-icon="inline-start" />}
          Toevoegen
        </Button>
        <Button onClick={onKlaar} size="sm" variant="ghost">
          Klaar
        </Button>
      </ItemActions>
    </Item>
  );
}

/** Een klant die er nog niet is. Zeldzaam, dus onderaan en niet in de weg. */
function NieuweOpdrachtgever({ onFout }: { onFout: (melding: string) => void }) {
  const maak = useMaakOpdracht();
  const [gever, setGever] = useState('');
  const [opdracht, setOpdracht] = useState('');

  const kanOpslaan = Boolean(gever.trim() && opdracht.trim()) && !maak.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Nieuwe opdrachtgever</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            onFout('');
            try {
              await maak.mutateAsync({ opdrachtgever: gever, opdracht });
              setGever('');
              setOpdracht('');
            } catch (err) {
              onFout(err instanceof ApiError ? err.message : String(err));
            }
          }}
        >
          <Field className="w-full sm:w-56">
            <FieldLabel htmlFor="nieuwe-opdrachtgever">Opdrachtgever</FieldLabel>
            <Input
              autoComplete="off"
              id="nieuwe-opdrachtgever"
              onChange={(e) => setGever(e.target.value)}
              value={gever}
            />
          </Field>
          <Field className="w-full sm:w-64">
            <FieldLabel htmlFor="nieuwe-opdrachtgever-opdracht">Eerste opdracht</FieldLabel>
            <Input
              autoComplete="off"
              id="nieuwe-opdrachtgever-opdracht"
              onChange={(e) => setOpdracht(e.target.value)}
              value={opdracht}
            />
          </Field>
          <Button disabled={!kanOpslaan} type="submit">
            {maak.isPending ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <PlusIcon data-icon="inline-start" />
            )}
            Toevoegen
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
